/**
 * Mailul în CRM, prin Roundcube (Madalin, 22.10.2026).
 *
 * Roundcube stă pe adresa lui (`mailcrm.boost4life.ro`), într-un cadru pe toată pagina. Nu pe
 * adresa CRM-ului: un mail venit din afară care ar găsi o breșă în Roundcube n-ajunge la contul de
 * CRM. Pagina primește mesaje doar de la adresa aceea și doar din cadrul ei (linkul spre un task:
 * și din cadrul de previzualizare din el).
 * Intrarea e automată: un token de unică folosință, schimbat de Roundcube direct cu serverul — vezi
 * `modules/webmail/webmail.routes.ts`. Butonul „Fă task" din Roundcube trimite aici datele mailului;
 * formularul e cel obișnuit, cu drepturile omului. Linkul „Vezi task-ul" dintr-un mail de la CRM
 * deschide task-ul aici, nu într-o filă nouă (#2419): Roundcube trimite doar numărul lui.
 *
 * Fără căsuță legată sau cu parola refuzată de serverul de mail, pagina arată direct formularul din
 * „Mailul meu": altfel omul ar da de o pagină goală și n-ar ști unde să caute.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { departamentulRolului, dinCadru, taskDinMesajMail } from '@b4l/shared'
import { api } from '../lib/api'
import { mesajEroare } from './taskuri/date'
import { useAuthStore } from '../stores/auth'
import { temaAleasa } from '../lib/tema'
import { tineMinteMailul } from '../lib/iesireMail'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { FormularTask } from './taskuri/Formulare'
import { CasutaMeaPage } from './CasutaMeaPage'

interface Stare {
  pornit: boolean
  origine: string
  casuta: {
    email: string
    activa: boolean
    areParola: boolean
    eroare: string | null
    /** Pusă de un admin pe adresa contului (ex. contact@): parola se schimbă din Setări. */
    deLaAdmin?: boolean
  } | null
}

interface MailPentruTask {
  messageId?: string
  subiect: string
  de: string
  data: string
  text: string
  atasamente: string[]
}

/** Câte reintrări automate (sesiune expirată) pe minut, ca o eroare să nu facă buclă. */
const REINTRARI_PE_MINUT = 3

/** Tema CRM-ului și în Roundcube: o duce intrarea, iar `crm_sso` o pune pe tema lui. */
function temaPentruMail(): 'dark' | 'light' {
  const t = temaAleasa()
  const intunecat =
    t === 'intunecat' ||
    (t === 'sistem' && (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false))
  return intunecat ? 'dark' : 'light'
}

export default function MailPage() {
  const eu = useAuthStore((s) => s.user)
  const poateTask = useAuthStore((s) => s.permissions.includes('tasks:write'))
  const alMeu = eu ? departamentulRolului(eu.role) : null
  const [mail, setMail] = useState<(MailPentruTask & { cheie: number }) | null>(null)
  const reintrari = useRef<number[]>([])
  const cadru = useRef<HTMLIFrameElement>(null)
  const navigate = useNavigate()

  const stare = useQuery({
    queryKey: ['webmail-stare'],
    queryFn: async () => (await api.get<Stare>('/webmail/stare')).data,
    // Reîmprospătarea la revenirea în fereastră ar putea scoate cadrul (și ciorna deschisă).
    refetchOnWindowFocus: false,
  })
  const s = stare.data
  const gata = !!s?.pornit && !!s.casuta?.activa && !!s.casuta.areParola && !s.casuta.eroare

  const intrare = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ url: string }>('/webmail/intrare', {
          tema: temaPentruMail(),
          // Fără el, Roundcube arată orele pe fusul serverului (UTC), nu pe al omului.
          fusOrar: fusOrarBrowser(),
        })
      ).data.url,
    onError: () => void stare.refetch(),
  })
  const { mutate: intra } = intrare

  // O singură intrare la deschiderea paginii; o reîmprospătare a stării nu mai deschide alta.
  useEffect(() => {
    if (gata) intra()
  }, [gata, intra])
  const origine = s?.origine
  useEffect(() => {
    if (gata && origine) tineMinteMailul(origine)
  }, [gata, origine])

  const reintra = useCallback(() => {
    const acum = Date.now()
    reintrari.current = reintrari.current.filter((t) => acum - t < 60_000)
    if (reintrari.current.length >= REINTRARI_PE_MINUT) return
    reintrari.current.push(acum)
    intra()
  }, [intra])

  useEffect(() => {
    const asculta = (e: MessageEvent) => {
      // Doar de la Roundcube-ul nostru: adresa lui și cadrul din pagina asta.
      if (!s || e.origin !== s.origine) return
      // Linkul spre un task stă în mailul din previzualizare, un cadru din cadrul nostru.
      const taskId = taskDinMesajMail(e.data)
      if (taskId) {
        if (dinCadru(e.source, cadru.current?.contentWindow)) navigate(`/task-uri?view=${taskId}`)
        return
      }
      if (e.source !== cadru.current?.contentWindow) return
      const d = e.data as { tip?: string; mail?: MailPentruTask }
      if (d?.tip === 'b4l-webmail-reintra') {
        // Sesiunea de mail a expirat, sau parola a fost refuzată: starea spune care.
        void stare.refetch().then((r) => {
          const c = r.data?.casuta
          if (r.data?.pornit && c?.activa && c.areParola && !c.eroare) reintra()
        })
        return
      }
      if (d?.tip !== 'b4l-webmail-task' || !d.mail) return
      if (!poateTask) {
        toast.error('N-ai drept să creezi task-uri. Cere-l administratorului.')
        return
      }
      setMail({ ...d.mail, cheie: Date.now() })
    }
    window.addEventListener('message', asculta)
    return () => window.removeEventListener('message', asculta)
  }, [poateTask, reintra, stare, s, navigate])

  if (stare.isLoading) return <p className="p-6 text-sm text-b4l-text-dim">Se deschide mailul…</p>
  // Eroare doar fără date: o reîmprospătare picată nu scoate cadrul deschis.
  if (!s) {
    return (
      <p className="p-6 text-sm text-b4l-red">
        {mesajEroare(stare.error, 'Nu s-a putut deschide mailul.')}
      </p>
    )
  }

  if (!s.pornit) {
    return (
      <Card>
        <p className="p-4 text-sm text-b4l-text-dim">
          Mailul în CRM nu e pornit încă pe server. Până atunci, folosește webmailul obișnuit.
        </p>
      </Card>
    )
  }

  // Casuta pusa de admin pe adresa contului: „Mailul meu” n-o poate schimba, Setarile da.
  if (!gata && s.casuta?.deLaAdmin) {
    return (
      <Card>
        <div className="space-y-3 p-4 text-sm">
          <p className="text-b4l-red-dark">
            {s.casuta.eroare
              ? 'Serverul de mail a refuzat parola căsuței.'
              : !s.casuta.areParola
                ? 'Căsuța n-are parola salvată.'
                : 'Căsuța e oprită.'}
          </p>
          <p className="text-b4l-text-dim">
            Căsuța {s.casuta.email} e conectată de un administrator, în Setări → SMTP/IMAP → Căsuțe
            suplimentare. Se repară acolo, apoi apeși „Încearcă din nou”.
          </p>
          <Button
            variant="secondary"
            onClick={() => void stare.refetch()}
            loading={stare.isFetching}
          >
            Încearcă din nou
          </Button>
        </div>
      </Card>
    )
  }

  if (!gata) {
    return (
      <div className="space-y-3">
        {s.casuta?.eroare && (
          <p className="max-w-2xl rounded-lg bg-b4l-red-light p-3 text-sm text-b4l-red-dark">
            {s.casuta.eroare} CRM-ul nu mai încearcă să intre cu parola veche, ca serverul de mail
            să nu blocheze căsuțele tuturor.
          </p>
        )}
        <CasutaMeaPage inMail laSalvat={() => void stare.refetch()} />
      </div>
    )
  }

  return (
    <div className="-m-4 h-[calc(100dvh-3.5rem)] md:-m-6">
      {intrare.data ? (
        <iframe
          ref={cadru}
          src={intrare.data}
          title="Mail"
          className="h-full w-full border-0"
          allow="clipboard-write"
        />
      ) : intrare.isError ? (
        <div className="space-y-3 p-6 text-sm">
          <p className="text-b4l-red">
            {mesajEroare(intrare.error, 'Nu s-a putut deschide mailul.')}
          </p>
          {/* Fara buton, o eroare trecatoare lasa pagina blocata pana la reincarcare. */}
          <Button variant="secondary" onClick={() => intra()} loading={intrare.isPending}>
            Încearcă din nou
          </Button>
        </div>
      ) : (
        <p className="p-6 text-sm text-b4l-text-dim">Se deschide mailul…</p>
      )}
      {mail && (
        <FormularTask
          key={mail.cheie}
          alMeu={alMeu}
          departamentInitial={alMeu}
          titluInitial={mail.subiect}
          dinMail={
            mail.messageId
              ? { messageId: mail.messageId, subiect: mail.subiect, expeditor: mail.de }
              : undefined
          }
          descriereInitiala={[
            `De la: ${mail.de}`,
            `Primit: ${mail.data}`,
            ...(mail.atasamente.length
              ? [`Atașamente în mail: ${mail.atasamente.join(', ')}`]
              : []),
            '',
            mail.text.trim(),
          ]
            .join('\n')
            .slice(0, 5000)}
          laInchidere={() => setMail(null)}
        />
      )}
    </div>
  )
}

/** Fusul orar al browserului, ex. `Europe/Bucharest`; `undefined` dacă browserul nu-l spune. */
function fusOrarBrowser(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}
